import { get, ref } from "firebase/database";
import { FIREBASE_DB_ROOT } from "../../App";
import { db } from "../../firebase";
import { Order } from "../../models/models";

export type PeriodPreset =
  | "today"
  | "yesterday"
  | "this-week"
  | "this-month"
  | "last-month"
  | "custom-date";

export interface StatsOrder extends Order {
  _sourceDate?: Date;
  CreatedAt?: string | number | Date;
  createdAt?: string | number | Date;
  deliveredAt?: string | number | Date;
  deliveredBy?: string;
}

export const PERIOD_OPTIONS: Array<{ label: string; value: PeriodPreset }> = [
  { label: "Danas", value: "today" },
  { label: "Jučer", value: "yesterday" },
  { label: "Ovaj tjedan", value: "this-week" },
  { label: "Ovaj mjesec", value: "this-month" },
  { label: "Prošli mjesec", value: "last-month" },
  { label: "Točan datum", value: "custom-date" },
];

const isObject = (value: unknown): value is Record<string, unknown> => {
  return typeof value === "object" && value !== null;
};

const normalizeDateToStartOfDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const getDateRange = (
  preset: PeriodPreset,
  customDate: Date,
): { start: Date; endExclusive: Date } => {
  const now = new Date();
  const todayStart = normalizeDateToStartOfDay(now);

  if (preset === "today") {
    const endExclusive = new Date(todayStart);
    endExclusive.setDate(endExclusive.getDate() + 1);
    return { start: todayStart, endExclusive };
  }

  if (preset === "yesterday") {
    const start = new Date(todayStart);
    start.setDate(start.getDate() - 1);
    return { start, endExclusive: todayStart };
  }

  if (preset === "this-week") {
    const day = todayStart.getDay();
    const diffToMonday = (day + 6) % 7;

    const start = new Date(todayStart);
    start.setDate(start.getDate() - diffToMonday);

    const endExclusive = new Date(start);
    endExclusive.setDate(endExclusive.getDate() + 7);

    return { start, endExclusive };
  }

  if (preset === "custom-date") {
    const start = normalizeDateToStartOfDay(customDate);
    const endExclusive = new Date(start);
    endExclusive.setDate(endExclusive.getDate() + 1);
    return { start, endExclusive };
  }

  if (preset === "last-month") {
    const start = new Date(
      todayStart.getFullYear(),
      todayStart.getMonth() - 1,
      1,
    );
    const endExclusive = new Date(
      todayStart.getFullYear(),
      todayStart.getMonth(),
      1,
    );
    return { start, endExclusive };
  }

  const start = new Date(todayStart.getFullYear(), todayStart.getMonth(), 1);
  const endExclusive = new Date(
    todayStart.getFullYear(),
    todayStart.getMonth() + 1,
    1,
  );
  return { start, endExclusive };
};

const buildDatesInRange = (start: Date, endExclusive: Date) => {
  const dates: Date[] = [];
  const cursor = new Date(start);

  while (cursor < endExclusive) {
    dates.push(new Date(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return dates;
};

const toOrderPath = (date: Date) => {
  return `${FIREBASE_DB_ROOT}/Narudžbe/${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

export const shouldCountOrder = (order: Order) => {
  return order.Recieved === true && order.Canceled !== true;
};

const epochToDate = (value: number): Date => {
  const timestampMs =
    Math.abs(value) < 1_000_000_000_000 ? value * 1000 : value;
  return new Date(timestampMs);
};

export const parseOrderDateTime = (
  value?: string | number | Date,
  fallbackDate?: Date,
): Date | null => {
  if (value === null || value === undefined) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const fromTimestamp = epochToDate(value);
    return Number.isNaN(fromTimestamp.getTime()) ? null : fromTimestamp;
  }

  if (typeof value !== "string") {
    return null;
  }

  const normalized = value.trim();
  if (!normalized) return null;

  if (/^\d{10,13}$/.test(normalized)) {
    const fromEpoch = epochToDate(Number(normalized));
    return Number.isNaN(fromEpoch.getTime()) ? null : fromEpoch;
  }

  const nums = normalized.match(/\d+/g)?.map(Number) ?? [];

  if (nums.length >= 5) {
    const [day, month, year, hour, minute] = nums;
    return new Date(year, month - 1, day, hour, minute);
  }

  if (nums.length >= 2 && fallbackDate) {
    const [hour, minute] = nums;
    const fallback = new Date(fallbackDate);
    fallback.setHours(hour, minute, 0, 0);
    return fallback;
  }

  if (/T|\d{4}-\d{1,2}-\d{1,2}/.test(normalized)) {
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

export const getOrderCreatedAt = (order: StatsOrder): Date | null => {
  const createdRaw = order.createdAt ?? order.CreatedAt ?? order.Vrijeme;
  return parseOrderDateTime(createdRaw, order._sourceDate);
};

export const getOrderDeliveredAt = (order: StatsOrder): Date | null => {
  const deliveredRaw = order.deliveredAt ?? order.DeliveredAt;
  return parseOrderDateTime(deliveredRaw, order._sourceDate);
};

export const fetchOrdersForPeriod = async (
  period: PeriodPreset,
  customDate: Date,
): Promise<StatsOrder[]> => {
  const { start, endExclusive } = getDateRange(period, customDate);
  const dates = buildDatesInRange(start, endExclusive);

  const snapshots = await Promise.all(
    dates.map((date) => get(ref(db, toOrderPath(date)))),
  );

  const orders: StatsOrder[] = [];

  snapshots.forEach((snapshot, index) => {
    const data = snapshot.val();
    if (!data || !isObject(data)) return;

    const sourceDate = dates[index];

    Object.entries(data).forEach(([key, value]) => {
      if (!isObject(value)) return;
      orders.push({
        ...(value as unknown as StatsOrder),
        OrderId: key,
        _sourceDate: sourceDate,
      });
    });
  });

  return orders;
};
